import { useContext, useState } from 'react';
import { SiteContentContext } from '../context/SiteContentContext';
import DOMPurify from 'dompurify';
import Icon from '../components/Icon';
import QuestionModal from '../components/QuestionModal';
import { injectIymcsMark } from '../utils/richText';
import { withIymcsMark } from '../components/IymcsMark';
import { useSeo, useJsonLd, toDescription } from '../hooks/useSeo';
import '../styles/fleet_page.css';
import '../styles/faq_page.css';

const renderFaqAnswer = (answer) => {
  if (!answer) return '';
  const html = /<[a-z][\s\S]*>/i.test(answer)
    ? DOMPurify.sanitize(answer)
    : answer.split('\n').filter(l => l.trim()).map(l => `<p>${l}</p>`).join('');
  return injectIymcsMark(html);
};

const TABS = [
  { key: 'adventure', label: 'Путешествия', icon: 'sail' },
  { key: 'study',     label: 'Обучение',    icon: 'anchor' },
  { key: 'race',      label: 'Регаты',      icon: 'trophy' },
  { key: 'general',   label: 'Общие',       icon: 'compass' },
];

const FaqPage = ({ isPreview = false }) => { // eslint-disable-line no-unused-vars
  useSeo({
    title: 'Вопросы и ответы',
    description: 'Ответы на частые вопросы об обучении яхтингу, парусных путешествиях и регатах The Sailing Doctor.',
  });
  const { content } = useContext(SiteContentContext);
  const faqItems = content.faqItems || [];
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState('adventure');
  const [isQuestionOpen, setIsQuestionOpen] = useState(false);

  // FAQPage — раскрывающиеся вопросы-ответы прямо в поисковой выдаче.
  useJsonLd(faqItems.length ? {
    '@type': 'FAQPage',
    mainEntity: faqItems
      .filter(i => i.question && i.answer)
      .map(i => ({
        '@type': 'Question',
        name: i.question,
        acceptedAnswer: { '@type': 'Answer', text: toDescription(i.answer, 5000) },
      })),
  } : null);

  const toggle = (id) => setOpenId(prev => prev === id ? null : id);

  const tabItems = faqItems.filter(i => (i.category || 'general') === tab);

  const hasTabs = TABS.some(t => faqItems.some(i => (i.category || 'general') === t.key));

  return (
    <main>
      <section className={`fleet-page-hero${content.faqHeroImage ? " has-cover" : ""}`} style={content.faqHeroImage ? { backgroundImage: `url(${content.faqHeroImage})` } : {}}>
        <h1>Вопросы и ответы</h1>
        <p>Ответы на самые частые вопросы о яхтинге и нашей школе</p>
      </section>
      <div className="fleet-page">

      {faqItems.length === 0 ? (
        <div className="fleet-page-empty">
          <p>FAQ пока не заполнен. Добавьте вопросы и ответы в <a href="/admin">админке</a> во вкладке ✏️ Контент → FAQ.</p>
        </div>
      ) : (
        <div className="faq-page-wrap">
          {hasTabs && (
            <div className="faq-tabs">
              {TABS.filter(t => faqItems.some(i => (i.category || 'general') === t.key)).map(t => (
                <button
                  key={t.key}
                  className={`faq-tab-btn ${tab === t.key ? 'active' : ''}`}
                  onClick={() => { setTab(t.key); setOpenId(null); }}
                >
                  <Icon name={t.icon} size={16} />
                  {t.label}
                </button>
              ))}
            </div>
          )}

          <div className="faq-accordion">
            {tabItems.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#888', padding: '40px 0' }}>
                В этом разделе пока нет вопросов.
              </p>
            ) : (
              tabItems.map(item => (
                <div key={item.id} className={`faq-item ${openId === item.id ? 'open' : ''}`}>
                  <button className="faq-question" onClick={() => toggle(item.id)}>
                    <span>{withIymcsMark(item.question)}</span>
                    <span className="faq-chevron">{openId === item.id ? '▲' : '▼'}</span>
                  </button>
                  {openId === item.id && (
                    <div className={`faq-answer${item.wide ? ' faq-answer--wide' : ''}`} dangerouslySetInnerHTML={{ __html: renderFaqAnswer(item.answer) }} />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      </div>
      <section className="cta-section">
        <h2>Не нашли ответ?</h2>
        <p>Задайте вопрос — ответим в течение дня.</p>
        <button className="cta-button" onClick={() => setIsQuestionOpen(true)}>Задать вопрос</button>
      </section>
      <QuestionModal isOpen={isQuestionOpen} onClose={() => setIsQuestionOpen(false)} pageTitle="FAQ" />
    </main>
  );
};

export default FaqPage;
